import { deportesDeJugador } from './deportesJugador'

// A quien le toca que prenda esta temporada.
//
// ANTES esto se deducia del plan de pago (plan -> conceptos -> prendas.concepto_id)
// dentro de getMatrizEntrega. Si el concepto no estaba en un plan, la prenda no
// existia para el modulo: el becado al que le sacaron el uniforme del plan, el club
// con patrocinador que los regala y el jugador sin plan asignado quedaban invisibles.
//
// AHORA sale del padron: quien esta inscrito, en que deporte, y que prendas van esta
// temporada para ese deporte. El plan solo responde "se cobra? ya pago?", que se
// calcula aparte.
//
// Funcion PURA a proposito: getMatrizEntrega trae los datos, esto los cruza. Asi se
// prueba sin mockear ocho tablas.
export const construirNecesidad = ({
  inscritos = [],
  temporadaPrendas = [],
  jugadores = [],
  asignaciones = [],
} = {}) => {
  const inscritosSet = new Set(inscritos)
  const jugadorById = Object.fromEntries((jugadores || []).map((j) => [j.id, j]))

  // La pieza del PLAN es unica por jugador+prenda+temporada -- lo garantiza el indice
  // `uq_jp_plan_unico` -- asi que se puede indexar por esa clave.
  //
  // Los EXTRAS no: un jugador puede comprar un segundo jersey de la misma prenda que
  // ya trae en su plan, o dos playeras de porra de distinta talla. Cada compra es una
  // celda propia. Antes todo compartia la clave jugador+prenda y el ultimo ganaba:
  // el segundo jersey -- el caso que justifica la pantalla de Extras -- desaparecia
  // de Pedidos y de Reparto sin avisar.
  const asigPlanPorClave = {}
  const asigExtras = []
  ;(asignaciones || []).forEach((a) => {
    if (a.origen === 'extra') asigExtras.push(a)
    else asigPlanPorClave[`${a.jugador_id}_${a.prenda_id}`] = a
  })

  const celdas = []
  const vistas = new Set()

  const push = (jugadorId, prendaId, esDeTemporada) => {
    const clave = `${jugadorId}_${prendaId}`
    if (vistas.has(clave)) return
    vistas.add(clave)
    const asig = asigPlanPorClave[clave] || null
    celdas.push({
      jugador_id: jugadorId,
      prenda_id: prendaId,
      origen: asig?.origen || 'plan',
      asignacionId: asig?.id || null,
      talla: asig?.talla || null,
      estatus: asig?.estatus || 'no_entregado',
      esDeTemporada,
    })
  }

  // Cada compra suelta, su propia celda. No pasa por `vistas`: no compite con la
  // parrilla ni con otro extra.
  const pushExtra = (a, esDeTemporada) => {
    celdas.push({
      jugador_id: a.jugador_id,
      prenda_id: a.prenda_id,
      origen: 'extra',
      asignacionId: a.id,
      talla: a.talla || null,
      estatus: a.estatus || 'no_entregado',
      esDeTemporada,
    })
  }

  // 1) La parrilla: cada inscrito x cada prenda ABIERTA de su deporte.
  const abiertas = (temporadaPrendas || []).filter((tp) => tp.estatus !== 'cerrada')
  ;(inscritos || []).forEach((jugadorId) => {
    const jugador = jugadorById[jugadorId]
    if (!jugador) return
    const deportes = deportesDeJugador({
      inscripciones: jugador.temporada_inscripciones,
      jugadoresGrupos: jugador.jugadores_grupos,
    })
    abiertas.forEach((tp) => {
      if (tp.deporte !== 'todos' && !deportes.includes(tp.deporte)) return
      push(jugadorId, tp.prenda_id, true)
    })
  })

  // 2) Todo lo ya capturado sigue apareciendo, aunque su prenda se haya cerrado o el
  //    jugador ya no este en el padron: si no, se perderia la posibilidad de
  //    entregarle lo que ya se pidio y pago.
  ;(asignaciones || []).forEach((a) => {
    const esDeTemporada = inscritosSet.has(a.jugador_id)
      && abiertas.some((tp) => tp.prenda_id === a.prenda_id)
    if (a.origen === 'extra') return   // los extras se agregan aparte, sin deduplicar
    push(a.jugador_id, a.prenda_id, esDeTemporada)
  })

  // 3) Las compras sueltas, cada una por su cuenta.
  asigExtras.forEach((a) => {
    const esDeTemporada = inscritosSet.has(a.jugador_id)
      && abiertas.some((tp) => tp.prenda_id === a.prenda_id)
    pushExtra(a, esDeTemporada)
  })

  return celdas
}

// Convierte una celda en la fila de `entregas` que consumen las pantallas.
//
// Se mantiene la MISMA forma de siempre (cuatro pantallas dependen de ella) y se
// agregan los DOS CANDADOS que antes estaban aplanados en un solo `pagado`:
//
//   prendaPagada      -> el dinero del jersey. Decide QUE SE MANDA A HACER.
//                        true / false / null (null = disuelto en el plan, no hay
//                        pago propio que consultar; NO es lo mismo que no pagar).
//   ligadoAPago       -> a que mensualidad esta amarrada su ENTREGA (o null).
//   condicionCumplida -> si esa mensualidad ya se cubrio.
//
// `pagado` se conserva para las pantallas de entrega y ahora significa "cumple lo
// requerido para entregarse": el jersey pagado Y la condicion cumplida si la tiene.
export const entregaDesdeCelda = (celda, ctx = {}) => {
  const {
    prenda = {}, asig = {}, pedido = null, linea = null, entregadosDeLinea = 0,
    prendaPagada = null, ligadoAPago = null, condicionCumplida = null,
  } = ctx
  return {
    prendaPagada,
    ligadoAPago,
    condicionCumplida,
    pagado: prendaPagada === true && condicionCumplida !== false,
    id: celda.asignacionId || `plan-${celda.jugador_id}-${celda.prenda_id}`,
    asignacionId: celda.asignacionId,
    jugador_id: celda.jugador_id,
    prenda_id: celda.prenda_id,
    origen: celda.origen,
    cantidad: asig.cantidad || 1,
    talla: celda.talla,
    estatus: celda.estatus,
    grupo_id: asig.grupo_id || null,
    numero_impreso: asig.numero_impreso || null,
    nickname_impreso: asig.nickname_impreso || null,
    pedido_id: asig.pedido_id || null,
    recibido_at: asig.recibido_at || null,
    entregado_at: asig.entregado_at || null,
    entregado_por: asig.entregado_por || null,
    linea_id: asig.linea_id || null,
    personalizada: prenda.personalizada !== false,
    pedido_estado: pedido?.estado || null,
    proveedor: pedido?.proveedor || null,
    linea,
    entregados_de_linea: entregadosDeLinea,
  }
}
